/**
 * Transfer-detection hooks (Phase 3, §6.5 / §6.6).
 *
 * Queries: `useTransferCandidates` lists the live self-join of unlinked
 * candidate pairs (`GET /api/transfers/candidates`); `useLinkedTransfers` lists
 * already-linked groups (`GET /api/transfers`). Mutations go through the P1
 * `api` wrapper (which attaches the `X-Requested-With` CSRF header):
 *   linkTransfer   → POST /api/transfers/link
 *   unlinkTransfer → POST /api/transfers/{group_id}/unlink
 *   detectTransfers→ POST /api/transfers/detect
 *
 * Linking/unlinking moves ledger rows in and out of the excluded Transfer
 * bucket, so every mutation invalidates the full downstream set (§6.6):
 * candidates, linked, transactions, inbox, reports and budgets.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '../api.ts';
import { qk, type TransferCandidatesQuery } from '../queryKeys.ts';
import type {
  TransferCandidateOut,
  TransferDetectIn,
  TransferDetectOut,
  TransferGroupOut,
  TransferLinkIn,
} from '../types.ts';

const STALE = 30 * 1000;

/** Invalidate `['transfers','candidates',*]` regardless of query params. */
const CANDIDATES_PREFIX = ['transfers', 'candidates'] as const;

/** GET /api/transfers/candidates — the live review queue (never persisted). */
export function useTransferCandidates(
  params: TransferCandidatesQuery = {},
): UseQueryResult<TransferCandidateOut[]> {
  return useQuery({
    queryKey: qk.transferCandidates(params),
    queryFn: () => {
      const search = new URLSearchParams();
      if (params.window_days != null) search.set('window_days', String(params.window_days));
      if (params.min_confidence) search.set('min_confidence', params.min_confidence);
      if (params.limit != null) search.set('limit', String(params.limit));
      const qs = search.toString();
      return api.get<TransferCandidateOut[]>(
        qs ? `/api/transfers/candidates?${qs}` : '/api/transfers/candidates',
      );
    },
    staleTime: STALE,
  });
}

/** GET /api/transfers — linked groups (review / unlink list). */
export function useLinkedTransfers(): UseQueryResult<TransferGroupOut[]> {
  return useQuery({
    queryKey: qk.linkedTransfers,
    queryFn: () => api.get<TransferGroupOut[]>('/api/transfers'),
    staleTime: STALE,
  });
}

/** Invalidate every cache a link/unlink touches (§6.6). */
function invalidateTransferDownstream(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: CANDIDATES_PREFIX });
  void qc.invalidateQueries({ queryKey: qk.linkedTransfers });
  void qc.invalidateQueries({ queryKey: qk.transactions });
  void qc.invalidateQueries({ queryKey: qk.inbox });
  void qc.invalidateQueries({ queryKey: qk.reports });
  void qc.invalidateQueries({ queryKey: qk.budgets });
}

/** POST /api/transfers/link — link a candidate pair into the Transfer bucket. */
export function useLinkTransfer(): UseMutationResult<TransferGroupOut, Error, TransferLinkIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TransferLinkIn) => api.post<TransferGroupOut>('/api/transfers/link', body),
    onSuccess: () => invalidateTransferDownstream(qc),
  });
}

/** POST /api/transfers/{group_id}/unlink — split a linked group back apart. */
export function useUnlinkTransfer(): UseMutationResult<TransferGroupOut, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (groupId: string) =>
      api.post<TransferGroupOut>(`/api/transfers/${groupId}/unlink`),
    onSuccess: () => invalidateTransferDownstream(qc),
  });
}

/** POST /api/transfers/detect — run a full auto-link pass over the ledger. */
export function useDetectTransfers(): UseMutationResult<
  TransferDetectOut,
  Error,
  TransferDetectIn | undefined
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body?: TransferDetectIn) =>
      api.post<TransferDetectOut>('/api/transfers/detect', body ?? {}),
    onSuccess: () => invalidateTransferDownstream(qc),
  });
}
